import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetProgramsId200 } from '@eureca/recruiter-api-sdk/types';

export type ProgramById = GetProgramsId200;

/**
 * Error surfaced when the BFF returns a non-2xx response.
 * `status` lets the route loader branch — 404 renders a not-found fallback.
 */
export class ProgramByIdFetchError extends Error {
  readonly name = 'ProgramByIdFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function fetchProgramById(id: string, signal?: AbortSignal): Promise<ProgramById> {
  const res = await fetchClient<ProgramById>({
    method: 'GET',
    url: `/programs/${id}`,
    signal,
  });
  if (res.error) {
    throw new ProgramByIdFetchError(
      `Failed to load program (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

export function programByIdQueryKey(id: string) {
  return ['recruiter', 'programs', 'id', id] as const;
}

export function programByIdQueryOptions(id: string) {
  return queryOptions({
    queryKey: programByIdQueryKey(id),
    queryFn: ({ signal }) => fetchProgramById(id, signal),
    staleTime: 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ProgramByIdFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
